import { useCallback, useEffect, useRef, useMemo, useState } from 'react';

// Pre-generated random values for better performance
const RANDOM_VALUES_POOL_SIZE = 1000;
const preGeneratedValues = Array.from({ length: RANDOM_VALUES_POOL_SIZE }, () => Math.random());
let randomIndex = 0;

export const getRandomValue = (): number => {
  randomIndex = (randomIndex + 1) % RANDOM_VALUES_POOL_SIZE;
  return preGeneratedValues[randomIndex];
};

export const getRandomInRange = (min: number, max: number): number => {
  return min + getRandomValue() * (max - min);
};

// Hardware acceleration styles
export const hardwareAcceleration = {
  transform: 'translateZ(0)',
  willChange: 'transform, opacity',
  backfaceVisibility: 'hidden' as const,
  perspective: '1000px',
};

export const animationOptimization = {
  ...hardwareAcceleration,
  contain: 'layout style paint' as const,
};

// Optimized animation frame handling
export const useAnimationFrame = (callback: (time: number) => void, deps: React.DependencyList = []) => {
  const requestRef = useRef<number | undefined>(undefined);
  const previousTimeRef = useRef<number | undefined>(undefined);
  
  const animate = useCallback((time: number) => {
    if (previousTimeRef.current !== undefined) {
      callback(time - previousTimeRef.current);
    }
    previousTimeRef.current = time;
    requestRef.current = requestAnimationFrame(animate);
  }, [callback, ...deps]);

  useEffect(() => {
    requestRef.current = requestAnimationFrame(animate);
    return () => {
      if (requestRef.current) {
        cancelAnimationFrame(requestRef.current);
      }
    };
  }, [animate]);
};

// Optimized interval with RAF
export const useOptimizedInterval = (callback: () => void, delay: number) => {
  const intervalRef = useRef<NodeJS.Timeout | undefined>(undefined);
  const callbackRef = useRef(callback);
  
  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  useEffect(() => {
    if (delay > 0) {
      intervalRef.current = setInterval(() => {
        callbackRef.current();
      }, delay);
    }
    
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [delay]);
};

// Memoized array generation
export const useMemoizedArray = <T>(generator: () => T, length: number, deps: React.DependencyList = []): T[] => {
  return useMemo(() => {
    return Array.from({ length }, generator);
  }, [length, generator, ...deps]);
};

// Debounced resize handler
export const useDebounceResize = (callback: () => void, delay: number = 150) => {
  const timeoutRef = useRef<NodeJS.Timeout | undefined>(undefined);
  const callbackRef = useRef(callback);
  
  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);
  
  useEffect(() => {
    const handleResize = () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      timeoutRef.current = setTimeout(() => callbackRef.current(), delay);
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [delay]);
};

// Pre-computed colors for better performance
export const generateColorPalette = (colors: string[], count: number): string[] => {
  const palette: string[] = [];
  for (let i = 0; i < count; i++) {
    palette.push(colors[i % colors.length]);
  }
  return palette;
};

// Optimized transform generator
export const generateTransform = (
  translateX: number = 0,
  translateY: number = 0,
  translateZ: number = 0,
  rotateX: number = 0,
  rotateY: number = 0,
  rotateZ: number = 0,
  scale: number = 1
): string => {
  return `translate3d(${translateX}px, ${translateY}px, ${translateZ}px) rotateX(${rotateX}deg) rotateY(${rotateY}deg) rotateZ(${rotateZ}deg) scale(${scale})`;
};

// Intersection Observer for performance
export const useIntersectionObserver = (
  elementRef: React.RefObject<Element>,
  options: IntersectionObserverInit = {}
) => {
  const [isIntersecting, setIsIntersecting] = useState(false);

  useEffect(() => {
    const element = elementRef.current;
    if (!element) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsIntersecting(entry.isIntersecting);
      },
      {
        threshold: 0.1,
        rootMargin: '50px',
        ...options,
      }
    );

    observer.observe(element);
    return () => observer.disconnect();
  }, [elementRef, options.threshold, options.rootMargin]);

  return isIntersecting;
};

// CSS-in-JS performance optimization
export const createOptimizedStyles = (styles: Record<string, React.CSSProperties>) => {
  return {
    ...styles,
    ...animationOptimization,
  };
};